import { api } from './api';
import { ClienteDocumento, ClienteDocumentoFormData, ClienteDocumentoFilters, ClienteDocumentoStats, ApiResponse } from '../types';

export const clienteDocumentoService = {
  // Listar documentos de um cliente
  async listarPorCliente(clienteId: number, filters?: ClienteDocumentoFilters): Promise<ApiResponse<ClienteDocumento[]>> {
    const params = new URLSearchParams();
    if (filters?.categoria) params.append('categoria', filters.categoria);
    if (filters?.search) params.append('search', filters.search);

    const response = await api.get(`/cliente-documentos/cliente/${clienteId}?${params.toString()}`);
    // Garante que sempre retorna um objeto com success/data/message
    if (Array.isArray(response.data)) {
      return {
        success: true,
        data: response.data,
        message: 'Documentos carregados com sucesso'
      };
    }
    return response.data;
  },

  // Upload de documento
  async upload(clienteId: number, formData: FormData): Promise<ApiResponse<ClienteDocumento>> {
    const response = await api.post(`/cliente-documentos/cliente/${clienteId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    // Se response.data tiver success, retorne ele
    if (response.data && typeof response.data.success !== 'undefined') {
      return response.data;
    }
    // Caso improvável: se response tiver success (sem data)
    if (response && typeof (response as any).success !== 'undefined') {
      return response as any;
    }
    // Fallback: erro genérico
    return { success: false, message: 'Erro inesperado no upload', data: undefined };
  },

  // Download de documento
  async download(id: number): Promise<Blob> {
    const response = await api.get(`/cliente-documentos/download/${id}`, {
      responseType: 'blob',
    });
    // Se response já for Blob, retorne ele diretamente
    if (response instanceof Blob) {
      return response;
    }
    // Caso contrário, retorne response.data (compatível com Axios padrão)
    return response.data;
  },

  // Atualizar informações do documento
  async atualizar(id: number, data: Partial<ClienteDocumento>): Promise<ApiResponse<ClienteDocumento>> {
    const response = await api.put(`/cliente-documentos/${id}`, data);
    // Se response.data tiver success, retorne ele
    if (response.data && typeof response.data.success !== 'undefined') {
      return response.data;
    }
    // Caso improvável: se response tiver success (sem data)
    if (response && typeof (response as any).success !== 'undefined') {
      return response as any;
    }
    // Fallback: erro genérico
    return { success: false, message: 'Erro inesperado ao atualizar', data: undefined };
  },

  // Excluir documento
  async excluir(id: number): Promise<ApiResponse<void>> {
    const response = await api.delete(`/cliente-documentos/${id}`);
    // Se response.data tiver success, retorne ele
    if (response.data && typeof response.data.success !== 'undefined') {
      return response.data;
    }
    // Caso improvável: se response tiver success (sem data)
    if (response && typeof (response as any).success !== 'undefined') {
      return response as any;
    }
    // Fallback: erro genérico
    return { success: false, message: 'Erro inesperado ao excluir', data: undefined };
  },

  // Obter estatísticas de documentos por cliente
  async estatisticas(clienteId: number): Promise<ApiResponse<ClienteDocumentoStats>> {
    const response = await api.get(`/cliente-documentos/cliente/${clienteId}/estatisticas`);
    return response.data;
  },

  // Função auxiliar para criar FormData para upload
  criarFormData(data: ClienteDocumentoFormData): FormData {
    const formData = new FormData();
    formData.append('arquivo', data.arquivo);
    if (data.descricao) formData.append('descricao', data.descricao);
    formData.append('categoria', data.categoria);
    return formData;
  },

  // Função auxiliar para formatar tamanho de arquivo
  formatarTamanho(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  },

  // Função auxiliar para obter ícone baseado no tipo de arquivo
  obterIcone(tipoArquivo: string): string {
    const tipos: Record<string, string> = {
      'application/pdf': 'picture_as_pdf',
      'application/msword': 'description',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'description',
      'application/vnd.ms-excel': 'table_chart',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'table_chart',
      'image/jpeg': 'image',
      'image/jpg': 'image',
      'image/png': 'image',
      'image/gif': 'image',
      'text/plain': 'article',
      'application/zip': 'folder_zip',
      'application/x-rar-compressed': 'folder_zip'
    };
    return tipos[tipoArquivo] || 'insert_drive_file';
  },

  // Função auxiliar para obter cor baseada na categoria
  obterCorCategoria(categoria: string): string {
    const cores: Record<string, string> = {
      contrato: '#1976d2',
      documento_pessoal: '#388e3c',
      comprovante: '#f57c00',
      outros: '#757575'
    };
    return cores[categoria] || '#757575';
  },

  // Função auxiliar para obter label da categoria
  obterLabelCategoria(categoria: string): string {
    const labels: Record<string, string> = {
      contrato: 'Contrato',
      documento_pessoal: 'Documento Pessoal',
      comprovante: 'Comprovante',
      outros: 'Outros'
    };
    return labels[categoria] || 'Outros';
  },

  // Função auxiliar para validar arquivo
  validarArquivo(arquivo: File): { valido: boolean; erro?: string } {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const tiposPermitidos = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'application/zip',
      'application/x-rar-compressed'
    ];

    if (arquivo.size > maxSize) {
      return { valido: false, erro: 'Arquivo muito grande. Tamanho máximo: 10MB' };
    }

    if (!tiposPermitidos.includes(arquivo.type)) {
      return { valido: false, erro: 'Tipo de arquivo não permitido' };
    }

    return { valido: true };
  }
};
